// User detail page — read-only profile with suspend/reactivate actions.
// Route: /t/:slug/admin/users/:userId

import { useParams, useNavigate } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useApi, ApiRequestError } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { usersApi } from '@/lib/api/usersRoles'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { FormSkeleton } from '@/components/shared/LoadingSkeleton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useToast } from '@/lib/hooks/use-toast'
import { ArrowLeft, UserX, UserCheck, Mail, Clock, Shield, CalendarDays, Key } from 'lucide-react'

const statusColors: Record<string, string> = {
  active: 'bg-green-100 text-green-800 border-green-200',
  pending: 'bg-amber-100 text-amber-800 border-amber-200',
  suspended: 'bg-red-100 text-red-800 border-red-200',
}

export function UserDetailPage() {
  const { slug } = useTenant()
  const { userId } = useParams({ strict: false }) as { userId: string }
  const navigate = useNavigate()
  const api = useApi()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const usersClient = usersApi(api)

  const [suspendOpen, setSuspendOpen] = useState(false)
  const [reactivateOpen, setReactivateOpen] = useState(false)
  const [disableApiOpen, setDisableApiOpen] = useState(false)
  const [revokeApiOpen, setRevokeApiOpen] = useState(false)

  const { data: user, error, isLoading, refetch } = useQuery({
    queryKey: queryKeys.users.detail(slug, userId),
    queryFn: () => usersClient.get(userId),
  })

  const suspendMutation = useMutation({
    mutationFn: () => usersClient.suspend(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(slug, userId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all(slug) })
      toast({ title: 'User suspended' })
      setSuspendOpen(false)
    },
    onError: (err) => {
      toast({
        title: 'Failed to suspend user',
        description: err instanceof ApiRequestError ? err.message : 'An unexpected error occurred.',
        variant: 'destructive',
      })
    },
  })

  const reactivateMutation = useMutation({
    mutationFn: () => usersClient.reactivate(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(slug, userId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all(slug) })
      toast({ title: 'User reactivated' })
      setReactivateOpen(false)
    },
    onError: (err) => {
      toast({
        title: 'Failed to reactivate user',
        description: err instanceof ApiRequestError ? err.message : 'An unexpected error occurred.',
        variant: 'destructive',
      })
    },
  })

  const invalidateUser = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(slug, userId) })
    queryClient.invalidateQueries({ queryKey: queryKeys.users.all(slug) })
  }

  const enableApiMutation = useMutation({
    mutationFn: () => usersClient.enableApi(userId),
    onSuccess: () => {
      invalidateUser()
      toast({ title: 'API access enabled' })
    },
    onError: (err) => {
      toast({
        title: 'Failed to enable API access',
        description: err instanceof ApiRequestError ? err.message : 'An unexpected error occurred.',
        variant: 'destructive',
      })
    },
  })

  const disableApiMutation = useMutation({
    mutationFn: () => usersClient.disableApi(userId),
    onSuccess: () => {
      invalidateUser()
      toast({ title: 'API access disabled' })
      setDisableApiOpen(false)
    },
    onError: (err) => {
      toast({
        title: 'Failed to disable API access',
        description: err instanceof ApiRequestError ? err.message : 'An unexpected error occurred.',
        variant: 'destructive',
      })
    },
  })

  const revokeApiMutation = useMutation({
    mutationFn: () => usersClient.revokeApiKey(userId),
    onSuccess: () => {
      invalidateUser()
      toast({ title: 'API key revoked' })
      setRevokeApiOpen(false)
    },
    onError: (err) => {
      toast({
        title: 'Failed to revoke API key',
        description: err instanceof ApiRequestError ? err.message : 'An unexpected error occurred.',
        variant: 'destructive',
      })
    },
  })

  if (isLoading) return <FormSkeleton />

  if (error) {
    return (
      <ErrorState
        message="Failed to load user"
        technicalDetail={error.message}
        onRetry={() => refetch()}
      />
    )
  }

  if (!user) return null

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate({ to: `/t/${slug}/admin/users` })}
          title="Back to users"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold">
              {user.display_name || user.email}
            </h1>
            <Badge variant="outline" className={statusColors[user.enrolment_status] ?? ''}>
              {user.enrolment_status}
            </Badge>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {user.enrolment_status === 'active' && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setSuspendOpen(true)}
            >
              <UserX className="mr-2 h-4 w-4" />
              Suspend
            </Button>
          )}
          {user.enrolment_status === 'suspended' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setReactivateOpen(true)}
            >
              <UserCheck className="mr-2 h-4 w-4" />
              Reactivate
            </Button>
          )}
        </div>
      </div>

      {/* Profile card */}
      <Card>
        <CardHeader>
          <CardTitle>Profile</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <ProfileField
              icon={<Mail className="h-4 w-4" />}
              label="Email"
              value={user.email}
            />
            <ProfileField
              icon={<Shield className="h-4 w-4" />}
              label="Enrolment method"
              value={user.enrolment_method ?? 'Not enrolled'}
            />
            <ProfileField
              icon={<CalendarDays className="h-4 w-4" />}
              label="Enrolled"
              value={user.enrolled_at ? new Date(user.enrolled_at).toLocaleString() : 'Not enrolled'}
            />
            <ProfileField
              icon={<Clock className="h-4 w-4" />}
              label="Last login"
              value={user.last_login_at ? new Date(user.last_login_at).toLocaleString() : 'Never'}
            />
            <ProfileField
              icon={<CalendarDays className="h-4 w-4" />}
              label="Created"
              value={new Date(user.created_at).toLocaleString()}
            />
            <div className="flex items-start gap-3">
              <div className="mt-0.5 text-muted-foreground">
                <Shield className="h-4 w-4" />
              </div>
              <div>
                <dt className="text-sm font-medium text-muted-foreground">Roles</dt>
                <dd className="text-sm">
                  {user.ownership_roles.length === 0 && user.role_names.length === 0 ? (
                    'None'
                  ) : (
                    <div className="flex flex-col gap-0.5">
                      {user.ownership_roles.map((name) => (
                        <span key={name} className="font-medium text-amber-700">{name}</span>
                      ))}
                      {user.ownership_roles.length > 0 && user.role_names.length > 0 && (
                        <div className="my-0.5 border-t border-border" />
                      )}
                      {user.role_names.map((name) => (
                        <span key={name}>{name}</span>
                      ))}
                    </div>
                  )}
                </dd>
              </div>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* API Access card — only for standard (human) users */}
      {user.user_type === 'standard' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              API Access
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!user.has_api_key ? (
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  API access is not enabled for this user.
                </p>
                <Button
                  size="sm"
                  onClick={() => enableApiMutation.mutate()}
                  disabled={enableApiMutation.isPending}
                >
                  {enableApiMutation.isPending ? 'Enabling...' : 'Enable API Access'}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium">Status:</span>
                  <Badge
                    variant="outline"
                    className={
                      user.api_key_status === 'active'
                        ? 'bg-green-100 text-green-800 border-green-200'
                        : user.api_key_status === 'revoked'
                          ? 'bg-red-100 text-red-800 border-red-200'
                          : 'bg-amber-100 text-amber-800 border-amber-200'
                    }
                  >
                    {user.api_key_status === 'unactivated'
                      ? 'Enabled — awaiting key generation'
                      : user.api_key_status === 'active'
                        ? 'Active'
                        : 'Revoked'}
                  </Badge>
                </div>
                {user.api_key_status === 'unactivated' && (
                  <p className="text-sm text-muted-foreground">
                    The user can now generate their API key from their settings page.
                  </p>
                )}
                {user.api_key_status === 'revoked' && (
                  <p className="text-sm text-muted-foreground">
                    This key has been revoked. To restore API access, disable and re-enable it.
                  </p>
                )}
                <div className="flex items-center gap-2">
                  {user.api_key_status === 'active' && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setRevokeApiOpen(true)}
                    >
                      Revoke Key
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDisableApiOpen(true)}
                  >
                    Disable API Access
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Disable API confirmation dialog */}
      <AlertDialog open={disableApiOpen} onOpenChange={setDisableApiOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disable API access</AlertDialogTitle>
            <AlertDialogDescription>
              Disabling API access for <strong>{user.display_name || user.email}</strong> will
              permanently delete their API key. They will need API access re-enabled to
              generate a new key.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={disableApiMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => disableApiMutation.mutate()}
              disabled={disableApiMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {disableApiMutation.isPending ? 'Disabling...' : 'Disable'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Revoke API key confirmation dialog */}
      <AlertDialog open={revokeApiOpen} onOpenChange={setRevokeApiOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke API key</AlertDialogTitle>
            <AlertDialogDescription>
              Revoking the API key for <strong>{user.display_name || user.email}</strong> will
              immediately invalidate it. The user will not be able to use the API until
              you disable and re-enable their access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revokeApiMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => revokeApiMutation.mutate()}
              disabled={revokeApiMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {revokeApiMutation.isPending ? 'Revoking...' : 'Revoke Key'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Suspend confirmation dialog */}
      <AlertDialog open={suspendOpen} onOpenChange={setSuspendOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Suspend user</AlertDialogTitle>
            <AlertDialogDescription>
              Suspending <strong>{user.display_name || user.email}</strong> will
              immediately revoke their access. They will not be able to log in
              until reactivated. Any active sessions will be invalidated.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={suspendMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => suspendMutation.mutate()}
              disabled={suspendMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {suspendMutation.isPending ? 'Suspending...' : 'Suspend'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reactivate confirmation dialog */}
      <AlertDialog open={reactivateOpen} onOpenChange={setReactivateOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reactivate user</AlertDialogTitle>
            <AlertDialogDescription>
              Reactivating <strong>{user.display_name || user.email}</strong> will
              restore their access. They will be able to log in and resume their
              previous role assignments.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reactivateMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => reactivateMutation.mutate()}
              disabled={reactivateMutation.isPending}
            >
              {reactivateMutation.isPending ? 'Reactivating...' : 'Reactivate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

// --- Helper component ---

function ProfileField({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode
  label: string
  value: string
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 text-muted-foreground">{icon}</div>
      <div>
        <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
        <dd className="text-sm">{value}</dd>
      </div>
    </div>
  )
}
